import React, { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'

const InlineCode = styled.code`
  font-family: monospace;
  font-size: 0.9em;
  padding: 1px 5px;
  border-radius: 4px;
  background-color: rgba(125, 125, 125, 0.15);
`

const CodeBlock = styled.pre`
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  padding: 12px 14px;
  border-radius: 8px;
  background-color: rgba(125, 125, 125, 0.12);
  overflow-x: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin: 0;
`

const List = styled.ul`
  margin: 0;
  padding-left: 22px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Paragraph = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const Heading = styled.p`
  margin: 0;
  font-weight: 600;
`

const MarkdownLink = styled.a`
  color: inherit;
  text-decoration: underline;
  overflow-wrap: anywhere;

  &:hover {
    color: ${RADOM_COLORS.BLACK};
  }
`

const INLINE_PATTERN = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))/g
const LINK_PATTERN = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/

function renderInline (text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE_PATTERN).map((chunk, i) => {
    const key = `${keyPrefix}-${i}`
    if (chunk.startsWith('`') && chunk.endsWith('`') && chunk.length > 2) {
      return <InlineCode key={key}>{chunk.slice(1, -1)}</InlineCode>
    }
    if (chunk.startsWith('**') && chunk.endsWith('**') && chunk.length > 4) {
      return <strong key={key}>{chunk.slice(2, -2)}</strong>
    }
    if (chunk.startsWith('*') && chunk.endsWith('*') && chunk.length > 2) {
      return <em key={key}>{chunk.slice(1, -1)}</em>
    }
    const link = LINK_PATTERN.exec(chunk)
    if (link) {
      return <MarkdownLink key={key} href={link[2]} target='_blank' rel='noopener noreferrer'>
        {link[1]}
      </MarkdownLink>
    }
    return chunk
  })
}

interface Block {
  type: 'paragraph' | 'heading' | 'list' | 'code'
  lines: string[]
  ordered?: boolean
}

function parseBlocks (text: string): Block[] {
  const blocks: Block[] = []
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') {
      i++
      continue
    }

    if (line.trimStart().startsWith('```')) {
      const code: string[] = []
      i++
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        code.push(lines[i])
        i++
      }
      i++
      blocks.push({ type: 'code', lines: code })
      continue
    }

    const bullet = /^\s*(?:[-*]|\d+[.)])\s+/
    if (bullet.test(line)) {
      const items: string[] = []
      const ordered = /^\s*\d+[.)]\s+/.test(line)
      while (i < lines.length && bullet.test(lines[i])) {
        items.push(lines[i].replace(bullet, ''))
        i++
      }
      blocks.push({ type: 'list', lines: items, ordered })
      continue
    }

    if (/^#{1,4}\s+/.test(line)) {
      blocks.push({ type: 'heading', lines: [line.replace(/^#{1,4}\s+/, '')] })
      i++
      continue
    }

    const paragraph: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trimStart().startsWith('```') &&
      !bullet.test(lines[i]) &&
      !/^#{1,4}\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i])
      i++
    }
    blocks.push({ type: 'paragraph', lines: paragraph })
  }

  return blocks
}

interface IProps {
  text: string
}

const Markdown = ({ text }: IProps): ReactElement => {
  const blocks = parseBlocks(text)

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    {blocks.map((block, i) => {
      switch (block.type) {
      case 'code':
        return <CodeBlock key={i}>{block.lines.join('\n')}</CodeBlock>
      case 'list':
        return <List key={i} as={block.ordered ? 'ol' : 'ul'}>
          {block.lines.map((item, j) => <li key={j}>{renderInline(item, `${i}-${j}`)}</li>)}
        </List>
      case 'heading':
        return <Heading key={i}>{renderInline(block.lines[0], `${i}`)}</Heading>
      default:
        return <Paragraph key={i}>{renderInline(block.lines.join('\n'), `${i}`)}</Paragraph>
      }
    })}
  </div>
}

export default Markdown
